"use client";

import { useQuery } from "@urql/next";
import { cx } from "class-variance-authority";
import NextLink from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
	type ComponentProps,
	startTransition,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Button,
	DialogTrigger,
	MenuItem,
	Separator,
} from "react-aria-components";

import { MeDocument } from "@whitecircle/graphql/graphql";

import { useAuth } from "@/components/auth";
import { CreateTeam } from "@/components/create-team";
import { Dropdown, Item } from "@/components/dropdown";
import { Avatar } from "@/components/icons/avatar";
import { Check } from "@/components/icons/check";
import { Chevron } from "@/components/icons/chevron";
import { Deployments } from "@/components/icons/deployments";
import { Dot } from "@/components/icons/dot";
import { Home } from "@/components/icons/home";
import { Logout } from "@/components/icons/logout";
import { Playground } from "@/components/icons/playground";
import { Plus } from "@/components/icons/plus";
import { Policies } from "@/components/icons/policies";
import { Profile } from "@/components/icons/profile";
import { Sessions } from "@/components/icons/sessions";
import { Settings } from "@/components/icons/settings";
import { useReset } from "@/components/urql";

function Link({ children, ...props }: ComponentProps<typeof NextLink>) {
	const [, activeSegment] = usePathname().split("/");
	const [, segment] = props.href.toString().split("/");
	const active = segment === activeSegment;

	return (
		<NextLink
			className={cx(
				"flex animate px-3 items-center h-9 gap-1.5 rounded-full",
				"outline outline-transparent", // Always present but transparent
				"hover:outline-border/50", // Change to your desired color on hover
				"data-[active=true]:outline-border", // Same for active
				"hover:inset-shadow-glow/10 data-[active=true]:inset-shadow-glow/20",
				"data-[active=false]:[&_svg]:text-muted-foreground",
			)}
			data-active={active}
			{...props}
		>
			{children}
		</NextLink>
	);
}

export function Side({ email }: { email: string }) {
	const [{ data }] = useQuery({ query: MeDocument });
	const triggerRef = useRef(null);
	const router = useRouter();
	const auth = useAuth();
	const reset = useReset();
	const [isOpen, setIsOpen] = useState(false);

	const selectedKeys = useMemo(
		() => new Set(data?.team.id ? [data.team.id] : []),
		[data?.team.id],
	);

	return (
		<DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
			<div className="flex flex-col h-full w-full font-[450] px-3">
				<div className="text-18 font-display font-[650] px-3">White Circle</div>
				<div className="flex flex-col gap-2 mt-9">
					<Link href="/">
						<Home className="w-6" />
						Home
					</Link>
					<Link href="/settings">
						<Settings className="w-6" />
						Settings
					</Link>
					<div className="text-muted-foreground mt-8 pl-3">Setup</div>
					<Link href="/policies">
						<Policies className="w-6" />
						Policies
					</Link>
					<Link href="/deployments">
						<Deployments className="w-6" />
						Deployments
					</Link>
					<Link href="/playground">
						<Playground className="w-6" />
						Playground
					</Link>
					<div className="text-muted-foreground mt-8 pl-3">Insights</div>
					<Link href="/sessions">
						<Sessions className="w-6" />
						Sessions
					</Link>
				</div>
				<Dropdown
					selectedKeys={selectedKeys}
					placement="top right"
					className="flex items-center gap-2 mt-auto h-9"
					button={
						<Button className="flex items-center gap-5 w-full group px-3 group/button">
							<Dot className="w-6" />
							<span className="overflow-hidden overflow-ellipsis">{email}</span>
							<Chevron
								className="ml-auto text-muted-foreground w-5 group-aria-expanded/button:rotate-180"
								ref={triggerRef}
							/>
						</Button>
					}
				>
					{data?.me.teams.map((team) => (
						<Item
							id={team.id}
							key={team.id}
							className="data-selected:bg-white/4 group/item"
							onAction={() =>
								auth.teams.set({ teamId: team.id }).then(() =>
									startTransition(async () => {
										router.refresh();
										reset();
										router.push("/");
									}),
								)
							}
						>
							<div className="size-7 rounded-full inline-flex items-center justify-center overflow-hidden p-1.5">
								{team.image ? (
									<img
										src={team.image}
										alt=""
										className="size-full object-cover rounded-full"
									/>
								) : (
									<Avatar className="size-full" />
								)}
							</div>
							{team.name}
							<Check
								className="size-5 text-dark group-not-data-selected/item:hidden ml-auto"
								aria-hidden="true"
							/>
						</Item>
					))}
					{data?.me.email.endsWith("@whitecircle.ai") ? (
						<MenuItem
							onAction={() => {
								setIsOpen(true);
							}}
							className="cursor-pointer rounded-2 h-7 pl-1.5 pr-3 inset-shadow-glow hover:inset-shadow-glow/40 animate border inline-flex items-center justify-between mx-4 mr-auto"
						>
							<Plus className="w-6" />
							Create space
						</MenuItem>
					) : null}
					<Separator />
					<Item href="/settings/profile">
						<Profile className="w-7" />
						Profile
					</Item>
					<Item
						onAction={() => {
							auth.signOut().then(() => router.push("/login"));
						}}
					>
						<Logout className="w-7" />
						Log out
					</Item>
				</Dropdown>
			</div>
			<CreateTeam />
		</DialogTrigger>
	);
}
